'use client';

import React, {type FormEvent} from 'react';
import clsx from 'clsx';
import {useRouter, useSearchParams} from 'next/navigation';

import type {EventsFilterSearchParams} from '../../app/(frontend)/kalender/page';
import {cn} from '../../utils';
import {buttonClasses} from '../button';

import type {SimpleviewCategory, SimpleviewMunicipality} from './hero';
import {EventPageSelect} from './hero';

type Props = {
	categories?: SimpleviewCategory[];
	municipalities?: SimpleviewMunicipality[];
	filters?: EventsFilterSearchParams;
};

export const Filter = ({categories, municipalities, filters}: Props) => {
	const router = useRouter();
	const searchParams = useSearchParams();

	const handleFormChange = (e: FormEvent<HTMLFormElement>): void => {
		const newSearchParams = new URLSearchParams(searchParams);
		const formData = new FormData(e.currentTarget);

		const kategori = formData.get('kategori')?.toString() ?? 'alle';
		const kommune = formData.get('kommune')?.toString() ?? 'alle';
		const fraDato = formData.get('fraDato')?.toString() ?? 'alle';

		if (kategori && kategori !== 'alle') {
			newSearchParams.set('kategori', kategori);
		} else {
			newSearchParams.delete('kategori');
		}
		if (kommune && kommune !== 'alle') {
			newSearchParams.set('kommune', kommune);
		} else {
			newSearchParams.delete('kommune');
		}
		if (fraDato && fraDato !== 'alle') {
			newSearchParams.set('fraDato', fraDato);
		} else {
			newSearchParams.delete('fraDato');
		}

		router.replace(`?${newSearchParams.toString()}`, {scroll: false});
	};

	const handleResetForm = () => {
		const newSearchParams = new URLSearchParams(searchParams);
		newSearchParams.delete('kategori');
		newSearchParams.delete('kommune');
		newSearchParams.delete('fraDato');
		router.replace(`?${newSearchParams.toString()}`, {scroll: false});
	};

	// Using a key derived from filter values forces React to remount the form when
	// filters change. This ensures uncontrolled inputs (using defaultValue) reflect
	// the current values from URL search params, since they don't update on re-render.
	const formKey = `${filters?.kategori ?? ''}-${filters?.kommune ?? ''}-${filters?.fraDato ?? ''}`;

	return (
		<form
			key={formKey}
			method="get"
			className={clsx('grid', 'gap-4')}
			onChange={handleFormChange}
		>
			{categories?.length && (
				<EventPageSelect
					label="Kategori"
					id="kategori"
					name="kategori"
					value={filters?.kategori ?? ''}
				>
					<option value="alle">Alle kategorier</option>
					{categories.map((category) => (
						<option key={category.value} value={category.value}>
							{category.label}
						</option>
					))}
				</EventPageSelect>
			)}
			<Date
				label="Fra dato"
				id="fraDato"
				name="fraDato"
				value={filters?.fraDato ?? ''}
			/>
			{municipalities?.length && (
				<EventPageSelect
					label="Kommune"
					id="kommune"
					name="kommune"
					value={filters?.kommune ?? ''}
				>
					<option value="alle">Alle kommuner</option>
					{municipalities.map((municipality) => (
						<option
							key={municipality.value}
							value={municipality.value}
						>
							{municipality.label}
						</option>
					))}
				</EventPageSelect>
			)}

			<button
				type="reset"
				onClick={handleResetForm}
				className={cn(
					buttonClasses,
					'h-12',
					'lg:h-14',
					'justify-self-end',
					'!text-bodyMedium',
				)}
			>
				Nullstill
			</button>
		</form>
	);
};

type DateProps = {
	label: string;
	name: string;
	id: string;
	value?: string;
};

const Date = ({label, id, name, value}: DateProps) => {
	return (
		<div>
			<label
				htmlFor={id}
				className={clsx(
					'text-beige-500',
					'font-semibold',
					'ml-4',
					'mb-1',
					'block',
				)}
			>
				{label}
			</label>
			<input
				id={id}
				name={name}
				type="date"
				defaultValue={value}
				className={clsx(
					'w-full',
					'active:text-forestGreen-700',
					'bg-beige-500',
					'h-12',
					'lg:h-14',
					'px-6',
					'text-bodyMedium',
					'outline-none',
					'rounded-full',
					'border-forestGreen-700',
					'focus:border-forestGreen-700',
					'focus:ring-forestGreen-700',
					'focus:ring-2',
					'focus:outline-none',
					'focus:ring-offset-2',
					'placeholder:text-[#527A76]',
					'transition-all',
					'duration-200',
				)}
			/>
		</div>
	);
};
